
import React, { useState } from "react";
import { format } from "date-fns";
import { useTranslations } from "use-intl";
import { Calendar } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { cn } from "@/lib/utils";

interface DateRangeFilterProps {
  title: string;
  fromDate: string;
  toDate: string;
  onFromDateChange: (date: string) => void;
  onToDateChange: (date: string) => void;
}

const DateRangeFilter: React.FC<DateRangeFilterProps> = ({
  title,
  fromDate,
  toDate,
  onFromDateChange,
  onToDateChange,
}) => {
  const t = useTranslations("consultantPayments.dateRangeFilter");
  const [showFromDate, setShowFromDate] = useState(false);
  const [showToDate, setShowToDate] = useState(false);

  // Format date for display
  const formatDate = (dateStr: string) => {
    if (!dateStr) return "";
    try {
      return format(new Date(dateStr), "PPP");
    } catch (e) {
      return "";
    }
  };

  return (
    <div className="space-y-2">
      <label className="text-sm font-medium">{title}</label>
      <div className="flex space-x-2">
        <Popover open={showFromDate} onOpenChange={setShowFromDate}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              className="w-full justify-start text-left"
            >
              <Calendar className="mr-2 h-4 w-4" />
              {fromDate ? formatDate(fromDate) : t("fromDate")}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <CalendarComponent
              mode="single"
              selected={fromDate ? new Date(fromDate) : undefined}
              onSelect={(date) => {
                onFromDateChange(date ? format(date, 'yyyy-MM-dd') : '');
                setShowFromDate(false);
              }}
              autoFocus
              className={cn("p-3 pointer-events-auto")}
            />
          </PopoverContent>
        </Popover>
        
        <Popover open={showToDate} onOpenChange={setShowToDate}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              className="w-full justify-start text-left"
            >
              <Calendar className="mr-2 h-4 w-4" />
              {toDate ? formatDate(toDate) : t("toDate")}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <CalendarComponent
              mode="single"
              selected={toDate ? new Date(toDate) : undefined}
              onSelect={(date) => {
                onToDateChange(date ? format(date, 'yyyy-MM-dd') : '');
                setShowToDate(false);
              }}
              autoFocus
              className={cn("p-3 pointer-events-auto")}
            />
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
};

export default DateRangeFilter;
